/**
 * Customer Data Explorer (CDE) - Shared Components
 */

// Storage keys for localStorage (only for client-side caching)
const CDE_STORAGE_KEYS = {
  RECENT_COMPANIES: 'cde_recent_companies',
  SKIP_CONFIRMATION: 'cde_skip_confirmation',
};

// Free demo companies - no credits charged, always available
const FREE_DEMO_COMPANIES = [
  {
    domain: 'proxycurl.com',
    name: 'Proxycurl',
    description: 'LinkedIn data extraction API for developers',
    isFree: true,
    isDemo: true
  }
];

// Reputable SaaS companies for exploration examples
const EXPLORATION_COMPANIES = [
  { domain: 'notion.so', name: 'Notion', description: 'All-in-one workspace for notes, docs, and collaboration' },
  { domain: 'figma.com', name: 'Figma', description: 'Collaborative interface design tool' },
  { domain: 'linear.app', name: 'Linear', description: 'Modern issue tracking and project management' },
  { domain: 'vercel.com', name: 'Vercel', description: 'Frontend cloud platform for developers' },
  { domain: 'supabase.com', name: 'Supabase', description: 'Open source Firebase alternative with Postgres' },
  { domain: 'airtable.com', name: 'Airtable', description: 'Low-code platform for building apps' },
  { domain: 'loom.com', name: 'Loom', description: 'Async video messaging for work' },
  { domain: 'calendly.com', name: 'Calendly', description: 'Scheduling automation platform' },
  { domain: 'intercom.com', name: 'Intercom', description: 'Customer messaging and engagement platform' },
  { domain: 'amplitude.com', name: 'Amplitude', description: 'Product analytics and event tracking' },
  { domain: 'datadog.com', name: 'Datadog', description: 'Cloud monitoring and security platform' },
  { domain: 'twilio.com', name: 'Twilio', description: 'Cloud communications platform' },
  { domain: 'auth0.com', name: 'Auth0', description: 'Identity and access management platform' },
  { domain: 'plaid.com', name: 'Plaid', description: 'Financial data connectivity platform' },
  { domain: 'retool.com', name: 'Retool', description: 'Low-code platform for internal tools' },
  { domain: 'webflow.com', name: 'Webflow', description: 'Visual web development platform' },
  { domain: 'miro.com', name: 'Miro', description: 'Online collaborative whiteboard' },
  { domain: 'clickup.com', name: 'ClickUp', description: 'All-in-one productivity platform' },
];

// API helper for CDE dashboard endpoints
const CdeApi = {
  async getSavedLists(page = 1, perPage = 20) {
    const params = new URLSearchParams({ page, per_page: perPage });
    const res = await fetch(`/dashboard/customers/api/saved-lists?${params}`);
    return res.json();
  },

  async deleteSavedList(listId) {
    const res = await fetch(`/dashboard/customers/api/saved-lists/${listId}`, {
      method: 'DELETE'
    });
    return res.json();
  },

  async getCompanyCustomers(domain, cursor = null, pageSize = 200, onboardingFree = false) {
    const params = new URLSearchParams({ page_size: pageSize });
    if (cursor) params.set('cursor', cursor);
    if (onboardingFree) params.set('onboarding_free', 'true');

    const res = await fetch(`/dashboard/customers/api/company/${encodeURIComponent(domain)}?${params}`);
    return res.json();
  },

  async getCreditBalance() {
    const res = await fetch('/dashboard/customers/api/credit-balance');
    return res.json();
  },

  async checkSavedListByDomain(domain) {
    const res = await fetch(`/dashboard/customers/api/saved-lists/by-domain/${encodeURIComponent(domain)}`);
    return res.json();
  },

  async enableShare(listId) {
    const res = await fetch(`/dashboard/customers/api/saved-lists/${listId}/share`, {
      method: 'POST'
    });
    return res.json();
  },

  async disableShare(listId) {
    const res = await fetch(`/dashboard/customers/api/saved-lists/${listId}/share`, {
      method: 'DELETE'
    });
    return res.json();
  },

  async getShareStatus(listId) {
    const res = await fetch(`/dashboard/customers/api/saved-lists/${listId}/share`);
    return res.json();
  }
};

// Progress bar easing - fast start, slow finish
const calculateProgress = (elapsedMs, totalMs = 60000) => {
  const normalizedTime = elapsedMs / totalMs;
  // Exponential easing - fast start, asymptotic approach to 100%
  return Math.min(100, Math.round(100 * (1 - Math.exp(-3 * normalizedTime))));
};

// Helper functions for localStorage (client-side caching only)
const StorageHelpers = {
  getRecentCompanies: () => {
    try {
      return JSON.parse(localStorage.getItem(CDE_STORAGE_KEYS.RECENT_COMPANIES) || '[]');
    } catch { return []; }
  },

  addRecentCompany: (company) => {
    const recent = StorageHelpers.getRecentCompanies();
    // Filter by domain (unique identifier)
    const domain = company.domain || company.company_domain;
    const filtered = recent.filter(c => (c.domain || c.company_domain) !== domain);
    const updated = [{
      name: company.name || company.company_name,
      domain: domain,
      description: company.description || null,
      savedListId: company.savedListId || null,
      viewedAt: new Date().toISOString()
    }, ...filtered].slice(0, 10);
    localStorage.setItem(CDE_STORAGE_KEYS.RECENT_COMPANIES, JSON.stringify(updated));
    return updated;
  },

  getSkipConfirmation: () => {
    // Always return false to ensure double opt-in modal is always shown
    return false;
  },

  setSkipConfirmation: (value) => {
    // Disabled - modal always shows
  }
};

// GICS Industry Code Mapping (Global Industry Classification Standard)
const GICS_CODES = {
  10101010: "Oil & Gas Drilling",
  10101020: "Oil & Gas Equipment & Services",
  10102010: "Integrated Oil & Gas",
  10102020: "Oil & Gas Exploration & Production",
  10102030: "Oil & Gas Refining & Marketing",
  10102040: "Oil & Gas Storage & Transportation",
  10102050: "Coal & Consumable Fuels",
  15101010: "Commodity Chemicals",
  15101020: "Diversified Chemicals",
  15101030: "Fertilizers & Agricultural Chemicals",
  15101040: "Industrial Gases",
  15101050: "Specialty Chemicals",
  15102010: "Construction Materials",
  15103010: "Metal & Glass Containers",
  15103020: "Paper Packaging",
  15104010: "Aluminum",
  15104020: "Diversified Metals & Mining",
  15104025: "Copper",
  15104030: "Gold",
  15104040: "Precious Metals & Minerals",
  15104045: "Silver",
  15104050: "Steel",
  15105010: "Forest Products",
  15105020: "Paper Products",
  20101010: "Aerospace & Defense",
  20102010: "Building Products",
  20103010: "Construction & Engineering",
  20104010: "Electrical Components & Equipment",
  20104020: "Heavy Electrical Equipment",
  20105010: "Industrial Conglomerates",
  20106010: "Construction & Farm Machinery & Heavy Trucks",
  20106015: "Agricultural & Farm Machinery",
  20106020: "Industrial Machinery",
  20107010: "Trading Companies & Distributors",
  20201010: "Commercial Printing",
  20201050: "Environmental & Facilities Services",
  20201060: "Office Services & Supplies",
  20201070: "Diversified Support Services",
  20201080: "Security & Alarm Services",
  20202010: "Human Resource & Employment Services",
  20202020: "Research & Consulting Services",
  20301010: "Air Freight & Logistics",
  20302010: "Airlines",
  20303010: "Marine",
  20304010: "Railroads",
  20304020: "Trucking",
  20305010: "Airport Services",
  20305020: "Highways & Railtracks",
  20305030: "Marine Ports & Services",
  25101010: "Auto Parts & Equipment",
  25101020: "Tires & Rubber",
  25102010: "Automobile Manufacturers",
  25102020: "Motorcycle Manufacturers",
  25201010: "Casinos & Gaming",
  25201020: "Hotels, Resorts & Cruise Lines",
  25201030: "Leisure Facilities",
  25201040: "Restaurants",
  25202010: "Education Services",
  25202020: "Specialized Consumer Services",
  25301010: "Distributors",
  25301020: "Internet & Direct Marketing Retail",
  25301030: "Multiline Retail",
  25301040: "Specialty Retail",
  25302010: "Apparel, Accessories & Luxury Goods",
  25302020: "Footwear",
  25302030: "Textiles",
  25401010: "Consumer Electronics",
  25401020: "Home Furnishings",
  25401025: "Homebuilding",
  25401030: "Household Appliances",
  25401040: "Housewares & Specialties",
  25501010: "Leisure Products",
  25502010: "Photographic Products",
  25502020: "Toys",
  30101010: "Brewers",
  30101020: "Distillers & Vintners",
  30101030: "Soft Drinks",
  30201010: "Agricultural Products",
  30201020: "Packaged Foods & Meats",
  30202010: "Tobacco",
  30301010: "Household Products",
  30302010: "Personal Products",
  35101010: "Health Care Equipment",
  35101020: "Health Care Supplies",
  35102010: "Health Care Distributors",
  35102015: "Health Care Services",
  35102020: "Health Care Facilities",
  35102030: "Managed Health Care",
  35103010: "Health Care Technology",
  35201010: "Biotechnology",
  35202010: "Pharmaceuticals",
  35203010: "Life Sciences Tools & Services",
  40101010: "Diversified Banks",
  40101015: "Regional Banks",
  40102010: "Thrifts & Mortgage Finance",
  40201020: "Other Diversified Financial Services",
  40201030: "Multi-Sector Holdings",
  40201040: "Specialized Finance",
  40202010: "Consumer Finance",
  40203010: "Asset Management & Custody Banks",
  40203020: "Investment Banking & Brokerage",
  40203030: "Diversified Capital Markets",
  40203040: "Financial Exchanges & Data",
  40204010: "Mortgage REITs",
  40301010: "Insurance Brokers",
  40301020: "Life & Health Insurance",
  40301030: "Multi-line Insurance",
  40301040: "Property & Casualty Insurance",
  40301050: "Reinsurance",
  45101010: "Internet Software & Services",
  45102010: "IT Consulting & Other Services",
  45102020: "Data Processing & Outsourced Services",
  45103010: "Application Software",
  45103020: "Systems Software",
  45201020: "Communications Equipment",
  45202030: "Technology Hardware, Storage & Peripherals",
  45203010: "Electronic Equipment & Instruments",
  45203015: "Electronic Components",
  45203020: "Electronic Manufacturing Services",
  45203030: "Technology Distributors",
  45301010: "Semiconductor Equipment",
  45301020: "Semiconductors",
  50101010: "Alternative Carriers",
  50101020: "Integrated Telecommunication Services",
  50102010: "Wireless Telecommunication Services",
  50201010: "Advertising",
  50201020: "Broadcasting",
  50201030: "Cable & Satellite",
  50201040: "Publishing",
  50202010: "Movies & Entertainment",
  50202020: "Interactive Home Entertainment",
  50203010: "Interactive Media & Services",
  55101010: "Electric Utilities",
  55102010: "Gas Utilities",
  55103010: "Multi-Utilities",
  55104010: "Water Utilities",
  55105010: "Independent Power Producers & Energy Traders",
  55105020: "Renewable Electricity",
  60101010: "Diversified REITs",
  60101020: "Industrial REITs",
  60101030: "Hotel & Resort REITs",
  60101040: "Office REITs",
  60101050: "Health Care REITs",
  60101060: "Residential REITs",
  60101070: "Retail REITs",
  60101080: "Specialized REITs",
  60102010: "Diversified Real Estate Activities",
  60102020: "Real Estate Operating Companies",
  60102030: "Real Estate Development",
  60102040: "Real Estate Services"
};

// Helper to get industry display string (code + name)
const getIndustryDisplay = (code) => {
  if (!code) return null;
  const name = GICS_CODES[code];
  return name ? `${code} - ${name}` : String(code);
};

// Helper to get just the industry name (verbose string)
const getIndustryName = (code) => {
  if (!code) return null;
  return GICS_CODES[code] || null;
};

// Make helpers available globally for other scripts
window.GICS_CODES = GICS_CODES;
window.getIndustryDisplay = getIndustryDisplay;
window.getIndustryName = getIndustryName;
window.calculateProgress = calculateProgress;

// Consistent Company Avatar Component
const CompanyAvatar = ({ name, domain, size = 'md', className = '' }) => {
  const [imgError, setImgError] = React.useState(false);
  const sizes = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-14 h-14 text-xl'
  };

  if (domain && !imgError) {
    const sizeClasses = sizes[size].split(' ').filter(c => !c.startsWith('text-')).join(' ');
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
        alt={name}
        onError={() => setImgError(true)}
        className={`rounded-xl object-contain ${sizeClasses} ${className}`}
      />
    );
  }

  return (
    <div className={`rounded-xl bg-gradient-to-br from-purple-500 to-purple-700
                    flex items-center justify-center text-white font-semibold ${sizes[size]} ${className}`}>
      {name.charAt(0)}
    </div>
  );
};

// Domain normalization helper
const normalizeDomain = (input) => {
  if (!input) return null;
  let d = input.trim().toLowerCase();
  // Remove protocol
  d = d.replace(/^https?:\/\//, '');
  // Remove www.
  d = d.replace(/^www\./, '');
  // Remove path
  d = d.split('/')[0];
  // Basic validation - must have at least one dot
  if (!d.includes('.')) return null;
  return d || null;
};
window.normalizeDomain = normalizeDomain;

// Company Search Bar with free-form domain entry and suggestions from saved lists
const CompanySearchBar = ({ onSelect, placeholder = "Enter website URL (e.g., stripe.com)", size = "large", suggestions = [], searchApiUrl }) => {
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [validationError, setValidationError] = React.useState(null);
  const [apiResults, setApiResults] = React.useState([]);
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // Fetch search results from API when query changes (debounced)
  React.useEffect(() => {
    if (!searchApiUrl || query.trim().length < 2) {
      setApiResults([]);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`${searchApiUrl}?q=${encodeURIComponent(query.trim())}`)
        .then(r => r.json())
        .then(data => {
          if (data.results) setApiResults(data.results);
          else setApiResults([]);
        })
        .catch(() => setApiResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchApiUrl]);

  // Merge API results with static suggestions
  const filteredSuggestions = React.useMemo(() => {
    if (!query.trim()) return suggestions.slice(0, 6);

    // API results take priority
    const merged = [...apiResults];
    const apiDomains = new Set(apiResults.map(r => (r.domain || '').toLowerCase()));

    // Add static suggestions not already in API results
    const q = query.toLowerCase();
    suggestions.forEach(c => {
      const domain = (c.domain || c.company_domain || '').toLowerCase();
      const name = (c.name || c.company_name || '').toLowerCase();
      if (!apiDomains.has(domain) && (name.startsWith(q) || domain.startsWith(q))) {
        merged.push(c);
      }
    });

    return merged.slice(0, 6);
  }, [query, suggestions, apiResults]);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && filteredSuggestions.length > 0) {
        handleSelectSuggestion(filteredSuggestions[selectedIndex]);
      } else {
        // Free-form domain entry
        handleSubmit();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleSubmit = () => {
    // Use DomainValidator for validation with error feedback
    const result = window.DomainValidator ? window.DomainValidator.validate(query) : { valid: !!normalizeDomain(query), domain: normalizeDomain(query), error: 'Invalid domain' };
    if (!result.valid) {
      setValidationError(result.error);
      return;
    }
    setValidationError(null);
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onSelect({ domain: result.domain, name: result.domain });
  };

  const handleSelectSuggestion = (item) => {
    const domain = item.domain || item.company_domain;
    const name = item.name || item.company_name;
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onSelect({ domain, name, customerCount: item.customer_count || item.customerCount });
  };

  const isLarge = size === 'large';

  return (
    <div className="relative">
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="relative">
          <span className={isLarge
            ? "absolute left-5 top-1/2 transform -translate-y-1/2 text-zinc-400 text-lg select-none"
            : "absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400 text-sm select-none"
          }>
            <i className="ph ph-globe mr-1"></i>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setValidationError(null);
              setIsOpen(true);
              setSelectedIndex(-1);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`${isLarge
              ? "w-full px-6 py-5 pl-14 pr-24 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg text-zinc-800 placeholder-zinc-400"
              : "w-full px-4 py-3 pl-12 pr-20 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base text-zinc-800 placeholder-zinc-400"
            } ${validationError ? 'border-red-400' : 'border-zinc-300'}`}
          />
          <button
            type="submit"
            className={`absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium
                       ${isLarge ? 'px-4 py-2.5 text-base' : 'px-3 py-1.5 text-sm'}`}
          >
            Search
          </button>
        </div>
      </form>

      {/* Validation Error Display */}
      {validationError && (
        <p className="text-sm text-red-600 mt-2 flex items-center">
          <i className="ph ph-warning-circle mr-1.5"></i>
          {validationError}
        </p>
      )}

      {isOpen && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200
                     rounded-2xl shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-2 text-xs text-zinc-500 bg-zinc-50 border-b border-zinc-100">
            Previously explored companies
          </div>
          {filteredSuggestions.map((item, idx) => {
            const name = item.name || item.company_name;
            const domain = item.domain || item.company_domain;
            const count = item.customer_count || item.customerCount || 0;
            return (
              <div
                key={item.id || domain}
                onClick={() => handleSelectSuggestion(item)}
                className={`px-5 py-4 cursor-pointer flex items-center justify-between
                           ${idx === selectedIndex ? 'bg-purple-50' : 'hover:bg-zinc-50'}
                           ${idx !== filteredSuggestions.length - 1 ? 'border-b border-zinc-100' : ''}`}
              >
                <div className="flex items-center">
                  <CompanyAvatar name={name} domain={domain} size="md" className="mr-4" />
                  <div>
                    <div className="font-semibold text-zinc-900">{name}</div>
                    <div className="text-sm text-zinc-500">{domain}</div>
                  </div>
                </div>
                {count > 0 && (
                  <div className="flex items-center text-sm text-zinc-500">
                    <i className="ph ph-users mr-1"></i>
                    {count} customers
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Company Card Component
const CompanyCard = ({ company, onClick, onGetAnchorPosition }) => {
  const cardRef = React.useRef(null);

  const handleClick = (e) => {
    if (onGetAnchorPosition && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      onClick(company, { top: rect.bottom, left: rect.left, right: rect.right });
    } else {
      onClick(company);
    }
  };

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-purple-300
                 hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <CompanyAvatar name={company.name} domain={company.domain} size="lg" />
        <div className="flex items-center px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-xs font-medium">
          <i className="ph ph-coins mr-1"></i>
          ~{company.creditCost} credits
        </div>
      </div>

      <h3 className="font-semibold text-zinc-900 mb-1 group-hover:text-purple-600 transition-colors">
        {company.name}
      </h3>
      <p className="text-sm text-zinc-500 mb-3">{company.domain}</p>

      {company.description && (
        <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{company.description}</p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
        <div className="flex items-center text-sm text-zinc-600">
          <i className="ph ph-users mr-1.5"></i>
          {company.customerCount > 0 ? `${company.customerCount} customers` : 'No data yet'}
        </div>
        <span className="text-purple-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          View <i className="ph ph-arrow-right"></i>
        </span>
      </div>
    </div>
  );
};

// Exploration Card - for demo companies and recently searched companies
const ExplorationCard = ({ company, onClick, isFree, isDemo }) => {
  return (
    <div
      onClick={() => onClick(company)}
      className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-purple-300
                 hover:shadow-md transition-all cursor-pointer group relative"
    >
      {/* Free/Demo badges */}
      {(isFree || isDemo) && (
        <div className="absolute top-3 right-3 flex gap-1.5">
          {isFree && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              Free
            </span>
          )}
          {isDemo && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Demo
            </span>
          )}
        </div>
      )}

      <div className="flex items-center mb-3">
        <CompanyAvatar name={company.name} domain={company.domain} size="md" className="mr-3" />
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-zinc-900 group-hover:text-purple-600 transition-colors truncate">
            {company.name}
          </h4>
          <p className="text-sm text-zinc-500 truncate">{company.domain}</p>
        </div>
      </div>

      {company.description && (
        <p className="text-sm text-zinc-600 line-clamp-2">{company.description}</p>
      )}
    </div>
  );
};

// Credit Confirmation Modal - supports anchor positioning
// Types: 'view' (default), 'pagination', 'loadAll'
const CreditConfirmModal = ({ isOpen, company, onConfirm, onCancel, anchorPosition, type = 'view', creditCost, isProcessed = true, totalCustomers }) => {
  const [dontAskAgain, setDontAskAgain] = React.useState(false);
  const modalRef = React.useRef(null);

  // Handle Escape key to close modal
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onCancel]);

  if (!isOpen || !company) return null;

  // Calculate modal position based on anchor
  const modalStyle = anchorPosition ? {
    position: 'fixed',
    top: Math.min(anchorPosition.top + 8, window.innerHeight - 400),
    left: Math.max(8, Math.min(anchorPosition.left, window.innerWidth - 420)),
    transform: 'none',
    maxWidth: '420px',
    width: '100%'
  } : {};

  const isAnchored = !!anchorPosition;

  // Determine cost display based on type
  const getCostDisplay = () => {
    if (type === 'loadAll') {
      if (isProcessed && totalCustomers) {
        const estimatedCost = totalCustomers * 2;
        return {
          title: 'Load All Customer Data',
          description: `Loading all ${totalCustomers} customers will cost approximately`,
          cost: `${estimatedCost} credits`,
          costNote: '(2 credits per customer)',
          warning: null
        };
      } else {
        return {
          title: 'Load All Customer Data',
          description: 'Loading all customers will cost',
          cost: '2 credits per customer',
          costNote: null,
          warning: 'Total cost is undetermined as this company has not been fully processed. Customers will be loaded progressively.'
        };
      }
    } else if (type === 'pagination') {
      const cost = creditCost || 400;
      return {
        title: 'Load Next Page',
        description: 'Loading the next page of results will cost',
        cost: cost < 400 ? `${cost} credits` : `up to 400 credits`,
        costNote: null,
        warning: null
      };
    } else if (type === 'restart') {
      return {
        title: `Restart ${company?.name || 'Company'} Exploration`,
        description: 'This will start a fresh exploration of this company\'s customers. Credits will be charged for new customer data.',
        cost: '~1 credit base + 2 credits per new customer',
        costNote: null,
        warning: 'You will be charged for any new customers discovered beyond what you have already saved.'
      };
    } else {
      // Default 'view' type
      return {
        title: 'View Customer Data',
        description: `Viewing customers for ${company.name} will cost`,
        cost: '1 base credit + 2 credits per customer',
        costNote: null,
        warning: null
      };
    }
  };

  const costDisplay = getCostDisplay();

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onCancel} />
      <div className={isAnchored ? 'fixed z-50' : 'fixed inset-0 flex items-center justify-center z-50 p-4'} style={isAnchored ? modalStyle : {}}>
        <div ref={modalRef} className={`bg-white rounded-2xl shadow-xl ${isAnchored ? 'w-full' : 'max-w-md w-full'} p-6`}>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-4">
            <i className="ph ph-coins text-xl text-amber-600"></i>
          </div>

          <h2 className="text-lg font-semibold text-zinc-900 text-center mb-2">
            {costDisplay.title}
          </h2>

          <p className="text-zinc-600 text-center mb-2 text-sm">
            {costDisplay.description}
          </p>

          <p className="text-center mb-1">
            <span className="font-bold text-amber-600 text-lg">{costDisplay.cost}</span>
          </p>

          {costDisplay.costNote && (
            <p className="text-center text-xs text-zinc-500 mb-4">{costDisplay.costNote}</p>
          )}

          {costDisplay.warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start text-xs text-amber-700">
                <i className="ph ph-warning text-base mr-2 text-amber-500 mt-0.5"></i>
                {costDisplay.warning}
              </div>
            </div>
          )}

          {!costDisplay.warning && (
            <div className="bg-zinc-50 rounded-lg p-3 mb-4 mt-4">
              <div className="flex items-center text-xs text-zinc-600">
                <i className="ph ph-info text-base mr-2 text-zinc-400"></i>
                Credits are charged based on data retrieved.
              </div>
            </div>
          )}

          <label className="flex items-center mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={dontAskAgain}
              onChange={(e) => setDontAskAgain(e.target.checked)}
              className="w-4 h-4 rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="ml-2 text-sm text-zinc-600">I understand and do not need a double confirmation for future sessions</span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-zinc-200 text-zinc-700 rounded-lg
                        hover:bg-zinc-50 transition-colors font-medium text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg
                        hover:bg-purple-700 transition-colors font-medium text-sm"
            >
              {type === 'loadAll' ? 'Load All' : type === 'pagination' ? 'Load Page' : 'Confirm & View'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Make CreditConfirmModal available globally for other scripts
window.CreditConfirmModal = CreditConfirmModal;

// Skeleton Table Loading Component - shows blurred skeleton with progress overlay
const SkeletonTableLoading = ({ progress, companyName }) => {
  const skeletonRows = 8;

  return (
    <div className="relative">
      {/* Blurred skeleton table */}
      <div className="blur-sm opacity-40 pointer-events-none">
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-24"></div></th>
                <th className="text-left px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-20"></div></th>
                <th className="text-left px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-20"></div></th>
                <th className="text-left px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-32"></div></th>
              </tr>
            </thead>
            <tbody>
              {[...Array(skeletonRows)].map((_, idx) => (
                <tr key={idx} className="border-b border-zinc-100">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-xl bg-zinc-200 mr-3 animate-pulse"></div>
                      <div className="h-4 bg-zinc-200 rounded w-32 animate-pulse"></div>
                    </div>
                  </td>
                  <td className="px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-28 animate-pulse"></div></td>
                  <td className="px-6 py-4"><div className="h-6 bg-zinc-200 rounded-md w-20 animate-pulse"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-zinc-200 rounded w-40 animate-pulse"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Centered progress overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-2xl border border-zinc-200 max-w-sm w-full mx-4">
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin">
              <i className="ph-bold ph-spinner text-3xl text-purple-600"></i>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-zinc-900 text-center mb-2">
            Loading customers for {companyName}...
          </h3>

          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-600">Progress</span>
              <span className="font-medium text-zinc-900">{progress}%</span>
            </div>
            <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <p className="text-sm text-zinc-500 text-center">
            <i className="ph ph-info mr-1"></i>
            Don't worry if you close the tab - data will be saved to Saved Lists automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

// Customer Table Component - Append-style pagination, fixed column widths
const CustomerTable = ({ customers, hasMore, onLoadMore, onLoadAll, isLoadingMore, isLoadingAll, onCustomerClick, totalEstimated, isProcessed }) => {
  const [hoveredRow, setHoveredRow] = React.useState(null);

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-56" />
            <col className="w-44" />
            <col />
            <col className="w-40" />
          </colgroup>
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Company</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Website</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Description</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer, idx) => (
              <tr
                key={customer.id}
                className={`border-b border-zinc-100 hover:bg-purple-50/50 transition-colors
                           ${customer.website ? 'cursor-pointer' : ''}
                           ${idx === customers.length - 1 && !hasMore ? 'border-b-0' : ''}`}
                onClick={(e) => {
                  if (!customer.website) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  onCustomerClick(customer, { top: rect.bottom, left: rect.left, right: rect.right });
                }}
                onMouseEnter={() => setHoveredRow(customer.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <CompanyAvatar name={customer.name} domain={normalizeDomain(customer.website)} size="md" className="mr-3 shrink-0" />
                    <div className="min-w-0">
                      <span className="font-medium text-zinc-900 block truncate max-w-48" title={customer.name}>{customer.name}</span>
                      {customer.tagline && (
                        <div className="text-xs text-zinc-500 mt-0.5 truncate max-w-48" title={customer.tagline}>{customer.tagline}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {customer.website ? (
                    <button
                      onClick={(e) => handleWebsiteClick(e, customer.website)}
                      className="flex items-center text-purple-600 hover:text-purple-800 hover:underline max-w-40"
                    >
                      <span className="truncate" title={customer.website}>{customer.website}</span>
                      <i className="ph ph-arrow-square-out ml-1.5 text-xs shrink-0"></i>
                    </button>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="text-zinc-600 text-sm truncate block" title={customer.description}>{customer.description}</span>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  {customer.website && (
                    <span className={`text-purple-600 text-sm font-medium whitespace-nowrap inline-flex items-center transition-opacity ${hoveredRow === customer.id ? 'opacity-100' : 'opacity-0'}`}>
                      View Customers <i className="ph ph-arrow-right ml-1"></i>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with Load More / Load All */}
      <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between bg-zinc-50">
        <div className="text-sm text-zinc-600">
          Showing {customers.length} customers
          {totalEstimated && ` of ~${totalEstimated} estimated`}
        </div>

        <div className="flex items-center gap-3">
          {hasMore && !isLoadingMore && !isLoadingAll && (
            <>
              <button
                onClick={onLoadMore}
                className="px-4 py-2 border border-zinc-200 rounded-lg text-sm font-medium hover:bg-zinc-100 transition-colors"
              >
                Load More
              </button>
              <button
                onClick={onLoadAll}
                className="px-4 py-2 text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Load All
              </button>
            </>
          )}

          {(isLoadingMore || isLoadingAll) && (
            <div className="bg-white rounded-xl p-4 shadow-lg border border-purple-200 flex items-center gap-4">
              <div className="animate-spin">
                <i className="ph-bold ph-spinner text-2xl text-purple-600"></i>
              </div>
              <div>
                <div className="font-medium text-zinc-900">
                  {isLoadingAll ? 'Loading all customers...' : 'Loading more customers...'}
                </div>
                <div className="text-sm text-zinc-500">Please wait, this may take a moment</div>
              </div>
            </div>
          )}

          {!hasMore && customers.length > 0 && (
            <span className="text-sm text-zinc-500">
              All customers loaded
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Competitor Table Component - Shared table for competitor listings
const CompetitorTable = ({ competitors, hoveredRow, setHoveredRow, footer }) => {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-56" />
            <col className="w-44" />
            <col />
            <col className="w-36" />
          </colgroup>
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Company</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Website</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Competition Reason</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {competitors.map((competitor, idx) => {
              const cName = getCompetitorName(competitor);
              const cDomain = getCompetitorDomain(competitor);
              return (
                <tr
                  key={idx}
                  className={`border-b border-zinc-100 hover:bg-purple-50/50 transition-colors cursor-pointer ${idx === competitors.length - 1 ? 'border-b-0' : ''}`}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => { window.location.href = `/dashboard/explorer/${encodeURIComponent(cDomain)}`; }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {window.CompanyAvatar ? (
                        <CompanyAvatar name={cName} domain={cDomain} size="md" className="mr-3 shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center text-zinc-400 font-semibold mr-3 shrink-0">
                          {(cName || '?').charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <span className="font-medium text-zinc-900 block truncate max-w-48" title={cName}>{cName}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(competitor.website || `https://${cDomain}`, '_blank'); }}
                      className="flex items-center text-purple-600 hover:text-purple-800 hover:underline max-w-40"
                    >
                      <span className="truncate" title={cDomain}>{cDomain}</span>
                      <i className="ph ph-arrow-square-out ml-1.5 text-xs shrink-0"></i>
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-zinc-600 text-sm truncate block" title={formatCompetitionReason(competitor.competition_reason)}>{formatCompetitionReason(competitor.competition_reason)}</span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <span className={`text-purple-600 text-sm font-medium whitespace-nowrap inline-flex items-center transition-opacity ${hoveredRow === idx ? 'opacity-100' : 'opacity-0'}`}>
                      View Profile <i className="ph ph-arrow-right ml-1"></i>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
};

// Export Buttons Component
const ExportButtons = ({ onExport, compact = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`flex items-center gap-2 ${compact ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} border border-zinc-200 rounded-lg
                  font-medium hover:bg-zinc-50 transition-colors`}
      >
        <i className="ph ph-export"></i>
        {!compact && 'Export'}
        <i className={`ph ph-caret-down transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-zinc-200 rounded-lg shadow-lg z-50">
          <button
            onClick={(e) => { e.stopPropagation(); onExport('csv'); setIsOpen(false); }}
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 rounded-lg"
          >
            <i className="ph ph-file-csv text-green-600"></i>
            Download CSV
          </button>
        </div>
      )}
    </div>
  );
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, listId, companyName, initialIsPublic, initialShareUrl, initialExpiresAt, shareEndpointPrefix }) => {
  const endpointBase = shareEndpointPrefix || '/dashboard/customers/api/saved-lists';
  const [isPublic, setIsPublic] = React.useState(initialIsPublic || false);
  const [shareUrl, setShareUrl] = React.useState(initialShareUrl || null);
  const [expiresAt, setExpiresAt] = React.useState(initialExpiresAt || null);
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsPublic(initialIsPublic || false);
      setShareUrl(initialShareUrl || null);
      setExpiresAt(initialExpiresAt || null);
      setCopied(false);
    }
  }, [isOpen, initialIsPublic, initialShareUrl, initialExpiresAt]);

  const handleToggleShare = async () => {
    setLoading(true);
    try {
      if (isPublic) {
        const res = await fetch(`${endpointBase}/${listId}/share`, { method: 'DELETE' });
        const result = await res.json();
        if (result.success) {
          setIsPublic(false);
          setShareUrl(null);
        }
      } else {
        const res = await fetch(`${endpointBase}/${listId}/share`, { method: 'POST' });
        const result = await res.json();
        if (result.success) {
          setIsPublic(true);
          setShareUrl(result.share_url);
          setExpiresAt(result.expires_at);
        }
      }
    } catch (err) {
      console.error('Share toggle error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    const fullUrl = window.location.origin + shareUrl;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-zinc-900">Share List</h2>
            <button onClick={onClose} className="p-1 text-zinc-400 hover:text-zinc-600">
              <i className="ph ph-x text-xl"></i>
            </button>
          </div>

          <p className="text-sm text-zinc-600 mb-4">
            Share <span className="font-medium">{companyName}</span> customers publicly via link.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl mb-4">
            <div>
              <div className="font-medium text-zinc-900">Public sharing</div>
              <div className="text-sm text-zinc-500">Anyone with the link can view this list</div>
            </div>
            <button
              onClick={handleToggleShare}
              disabled={loading}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                isPublic ? 'bg-purple-600' : 'bg-zinc-300'
              } ${loading ? 'opacity-50' : ''}`}
            >
              <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                isPublic ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Share URL Section */}
          {isPublic && shareUrl && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Share link</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={window.location.origin + shareUrl}
                  className="flex-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-lg text-sm text-zinc-700"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center gap-1.5"
                >
                  <i className={`ph ${copied ? 'ph-check' : 'ph-copy'}`}></i>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {expiresAt && (
                <p className="text-xs text-zinc-500 mt-2">
                  <i className="ph ph-clock mr-1"></i>
                  Link expires {formatExpiryDate(expiresAt)}
                </p>
              )}
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
            <i className="ph ph-info mr-1.5"></i>
            Viewers must register to download as CSV.
          </div>
        </div>
      </div>
    </>
  );
};

// Empty State Component
const EmptyState = ({ icon, title, description, action }) => {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4">
        <i className={`ph ${icon} text-3xl text-zinc-400`}></i>
      </div>
      <h3 className="text-lg font-semibold text-zinc-900 mb-2">{title}</h3>
      <p className="text-zinc-500 mb-6 max-w-md mx-auto">{description}</p>
      {action}
    </div>
  );
};

// Recent Company Item - with export shortcut
const RecentCompanyItem = ({ company, onClick, onExport }) => {
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleExport = (format) => {
    if (onExport) onExport(company, format);
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 rounded-lg transition-colors group">
      <div
        className="flex items-center flex-1 cursor-pointer min-w-0"
        onClick={() => onClick(company)}
      >
        <CompanyAvatar name={company.name} domain={company.domain} size="sm" className="mr-3 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 text-sm">{company.name}</div>
          <div className="text-xs text-zinc-500">{company.domain}</div>
          {company.description && (
            <div className="text-xs text-zinc-400 truncate mt-0.5">{company.description}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        <span className="text-xs text-zinc-400 group-hover:hidden">{timeAgo(company.viewedAt)}</span>
        <div className="hidden group-hover:block">
          <ExportButtons onExport={handleExport} compact />
        </div>
      </div>
    </div>
  );
};

// Saved List Item - with export button, full width style
// Works with API response format: { id, company_name, company_domain, customer_count, updated_at, is_public, share_url }
const SavedListItem = ({ item, onView, onRemove, onExport, onShare, dataType }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Support both old (name, slug) and new (company_name, company_domain) formats
  const name = item.company_name || item.name;
  const domain = item.company_domain || item.domain;
  const isCompetitor = dataType === 'competitor';
  const itemCount = isCompetitor
    ? (item.competitor_count || item.customer_count || item.customerCount || 0)
    : (item.customer_count || item.customerCount || 0);
  const itemLabel = isCompetitor ? 'competitors' : 'customers';
  const savedDate = item.updated_at || item.savedAt || item.created_at;
  const isPublic = item.is_public || false;

  return (
    <div className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-xl hover:border-purple-300 transition-colors">
      <div className="flex items-center cursor-pointer" onClick={() => onView(item)}>
        <CompanyAvatar name={name} domain={domain} size="md" className="mr-4" />
        <div>
          <div className="font-medium text-zinc-900 flex items-center gap-2">
            {name}
            {isPublic && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                <i className="ph ph-globe mr-0.5"></i>Public
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-500">
            {itemCount} {itemLabel} {savedDate && <>&bull; Saved {formatDate(savedDate)}</>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={(e) => { e.stopPropagation(); onShare(item); }}
          className={`flex items-center gap-1.5 px-2 py-1 text-xs border rounded-lg font-medium hover:bg-zinc-50 transition-colors ${
            isPublic ? 'border-green-300 text-green-700' : 'border-zinc-200 text-zinc-600'
          }`}
          title={isPublic ? 'Manage share link' : 'Share this list'}
        >
          <i className={`ph ${isPublic ? 'ph-link' : 'ph-share-network'}`}></i>
          Share
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onExport(item); }}
          className="flex items-center gap-1.5 px-2 py-1 text-xs border border-zinc-200 rounded-lg font-medium hover:bg-zinc-50 transition-colors"
          title="Download CSV"
        >
          <i className="ph ph-file-csv text-green-600"></i>
          CSV
        </button>
        <button
          onClick={() => onView(item)}
          className="px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-800"
        >
          View
        </button>
        <button
          onClick={() => onRemove(item.id)}
          className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors"
        >
          <i className="ph ph-trash"></i>
        </button>
      </div>
    </div>
  );
};

// Helper to map relationship_type to human-readable label
const RELATIONSHIP_LABELS = {
  customer: 'Customer',
  investor: 'Investor',
  partner_platform: 'Partner/Platform',
};

const getRelationshipLabel = (type) => RELATIONSHIP_LABELS[type] || 'Customer';

// Filter customers by relationship type
const filterByRelationshipType = (customers, tab) => {
  return customers.filter(c => (c.relationship_type || 'customer') === tab);
};

// Relationship Category Tabs Component
const RelationshipTabs = ({ customers, activeTab, onTabChange }) => {
  const counts = React.useMemo(() => {
    const c = { customer: 0, investor: 0, partner_platform: 0 };
    customers.forEach(item => {
      const rt = item.relationship_type || 'customer';
      if (c[rt] !== undefined) c[rt]++;
    });
    return c;
  }, [customers]);

  const tabs = [
    { key: 'customer', label: 'Customers', count: counts.customer, icon: 'ph-users' },
    { key: 'investor', label: 'Investors', count: counts.investor, icon: 'ph-chart-line-up' },
    { key: 'partner_platform', label: 'Partners/Platforms', count: counts.partner_platform, icon: 'ph-handshake' },
  ];

  return (
    <div className="flex overflow-x-auto border-b border-zinc-200 mb-6">
      {tabs.map(tab => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`flex items-center gap-2 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 whitespace-nowrap
              ${isActive
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
              }`}
          >
            <i className={`ph ${tab.icon}`}></i>
            {tab.label}
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold
              ${isActive
                ? 'bg-purple-100 text-purple-700'
                : 'bg-zinc-100 text-zinc-500'
              }`}>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Export components to window for use by other scripts
window.CDE_STORAGE_KEYS = CDE_STORAGE_KEYS;
window.FREE_DEMO_COMPANIES = FREE_DEMO_COMPANIES;
window.EXPLORATION_COMPANIES = EXPLORATION_COMPANIES;
window.CdeApi = CdeApi;
window.StorageHelpers = StorageHelpers;
window.CompanyAvatar = CompanyAvatar;
window.CompanySearchBar = CompanySearchBar;
window.CompanyCard = CompanyCard;
window.ExplorationCard = ExplorationCard;
window.CreditConfirmModal = CreditConfirmModal;
window.SkeletonTableLoading = SkeletonTableLoading;
window.CustomerTable = CustomerTable;
window.CompetitorTable = CompetitorTable;
window.ExportButtons = ExportButtons;
window.ShareModal = ShareModal;
window.EmptyState = EmptyState;
window.RecentCompanyItem = RecentCompanyItem;
window.SavedListItem = SavedListItem;
window.RelationshipTabs = RelationshipTabs;
window.filterByRelationshipType = filterByRelationshipType;
window.getRelationshipLabel = getRelationshipLabel;
window.RELATIONSHIP_LABELS = RELATIONSHIP_LABELS;
